import { memo } from "react";
import { Div, Text, ToggleInput, useBooleanState, useMediaQuery, useTheme } from "react-better-html";

import { useI18n } from "../../../utils/i18n";

import Section from "../../../components/structure/Section";
import SettingItem from "./SettingItem";

type RowHolderProps = {
   children?: React.ReactNode;
};

const RowHolder = memo(function RowHolder({ children }: RowHolderProps) {
   const theme = useTheme();
   const mediaQuery = useMediaQuery();

   return (
      <Div.row
         width={mediaQuery.size600 ? 120 : 160}
         alignItems="center"
         justifyContent="space-evenly"
         gap={theme.styles.space}
      >
         {children}
      </Div.row>
   );
});

function NotificationsSection() {
   const theme = useTheme();
   const { t } = useI18n();

   const [priceChangesInApp, setPriceChangesInApp] = useBooleanState(true);
   const [priceChangesEmail, setPriceChangesEmail] = useBooleanState(true);
   const [availabilityChangesInApp, setAvailabilityChangesInApp] = useBooleanState(true);
   const [availabilityChangesEmail, setAvailabilityChangesEmail] = useBooleanState(false);
   const [activityDigestInApp, setActivityDigestInApp] = useBooleanState(false);
   const [activityDigestEmail, setActivityDigestEmail] = useBooleanState(true);

   return (
      <Section
         title={t((l) => l.pages.settings.sections.notifications.title)}
         description={t((l) => l.pages.settings.sections.notifications.description)}
         disabled
      >
         <Div.row alignItems="center" marginBottom={theme.styles.gap} paddingInline={theme.styles.space}>
            <Text flex={1} fontSize={14} color={theme.colors.textSecondary}>
               {t((l) => l.pages.settings.sections.notifications.headers.event)}
            </Text>

            <RowHolder>
               <Text fontSize={14} textAlign="center" color={theme.colors.textSecondary}>
                  {t((l) => l.pages.settings.sections.notifications.headers.inApp)}
               </Text>
               <Text fontSize={14} textAlign="center" color={theme.colors.textSecondary}>
                  {t((l) => l.pages.settings.sections.notifications.headers.email)}
               </Text>
            </RowHolder>
         </Div.row>

         <Div.column gap={theme.styles.gap}>
            <SettingItem
               isBox
               title={t((l) => l.pages.settings.sections.notifications.events.priceChanges)}
               description={t((l) => l.pages.settings.sections.notifications.events.priceChangesDescription)}
               rightElement={
                  <RowHolder>
                     <ToggleInput.switch checked={priceChangesInApp} onChange={setPriceChangesInApp.setState} />
                     <ToggleInput.switch checked={priceChangesEmail} onChange={setPriceChangesEmail.setState} />
                  </RowHolder>
               }
            />
            <SettingItem
               isBox
               title={t((l) => l.pages.settings.sections.notifications.events.availabilityChanges)}
               description={t((l) => l.pages.settings.sections.notifications.events.availabilityChangesDescription)}
               rightElement={
                  <RowHolder>
                     <ToggleInput.switch
                        checked={availabilityChangesInApp}
                        onChange={setAvailabilityChangesInApp.setState}
                     />
                     <ToggleInput.switch
                        checked={availabilityChangesEmail}
                        onChange={setAvailabilityChangesEmail.setState}
                     />
                  </RowHolder>
               }
            />
            <SettingItem
               isBox
               title={t((l) => l.pages.settings.sections.notifications.events.activityDigest)}
               description={t((l) => l.pages.settings.sections.notifications.events.activityDigestDescription)}
               rightElement={
                  <RowHolder>
                     <ToggleInput.switch checked={activityDigestInApp} onChange={setActivityDigestInApp.setState} />
                     <ToggleInput.switch checked={activityDigestEmail} onChange={setActivityDigestEmail.setState} />
                  </RowHolder>
               }
            />
         </Div.column>
      </Section>
   );
}

export default memo(NotificationsSection);
